import type { StoxlyClient } from '@stoxly/api-client';
import type { Body } from '../op-types';

export function createPositionMutations(client: StoxlyClient) {
  return {
    deletePosition: {
      /**
       * `keepRealized` (#891): keep the position's realized P&L in the portfolio history
       * (the backend's default). A bare id keeps it; pass `false` to drop it explicitly.
       *
       * FAILURE THE `String(...)` PREVENTS: the route has always read this parameter as TEXT
       * (`keepRealized !== 'false' && keepRealized !== '0'`) — the pre-zod
       * `@ApiQuery({ type: 'boolean' })` was a hand-written annotation that disagreed with its
       * own handler. The boolean stays this wrapper's argument, because that is what a caller
       * means; the spelling the route actually parses is produced here, once.
       */
      mutationFn: (
        vars:
          | string
          | { positionId: string; keepRealized?: boolean; acknowledgeCashEffect?: boolean },
      ) => {
        const { positionId, keepRealized, acknowledgeCashEffect } =
          typeof vars === 'string'
            ? { positionId: vars, keepRealized: undefined, acknowledgeCashEffect: undefined }
            : vars;
        return client.positionsDeletePosition(undefined, {
          params: { positionId },
          // An INLINE literal on purpose: TypeScript's excess-property check against the
          // generated `queries` type is what reddens a backend rename here. The ky runtime
          // drops `undefined` members, so an unset flag never reaches the wire.
          queries: {
            keepRealized: keepRealized === undefined ? undefined : String(keepRealized),
            // R3 — `queryBoolean()` on the route: a RAW boolean, unlike `keepRealized`'s text.
            // Set only after the user has seen the 409's cash effect.
            acknowledgeCashEffect,
          },
        });
      },
    },
    updatePosition: {
      mutationFn: ({
        positionId,
        body,
      }: {
        positionId: string;
        body: Body<'positionsUpdatePosition'>;
      }) => client.positionsUpdatePosition(body, { params: { positionId } }),
    },
    /**
     * #1945: re-run the import's own symbol resolver over the identity the statement supplied
     * and the row persists (`statementName` / `statementIsin` / `statementSymbol`) — no body; the
     * answer is applied through the ordinary symbol-edit path, so the identity guard still
     * applies. 422 `UNRESOLVED_POSITION` when the resolver still binds nothing.
     */
    reResolvePosition: {
      mutationFn: (positionId: string) =>
        client.positionsReResolvePosition(undefined, { params: { positionId } }),
    },
    // The name of a held fund unit (a PPK, PPE or TFI fund): the only class with a name of its own.
    renameFundUnit: {
      mutationFn: ({
        positionId,
        body,
      }: {
        positionId: string;
        body: Body<'positionsRenameFundUnit'>;
      }) => client.positionsRenameFundUnit(body, { params: { positionId } }),
    },
    updatePositionCore: {
      mutationFn: ({
        positionId,
        body,
      }: {
        positionId: string;
        body: Body<'positionsUpdatePositionCore'>;
      }) => client.positionsUpdatePositionCore(body, { params: { positionId } }),
    },
    updatePositionNotes: {
      mutationFn: ({
        positionId,
        body,
      }: {
        positionId: string;
        body: Body<'positionsUpdatePositionNotes'>;
      }) => client.positionsUpdatePositionNotes(body, { params: { positionId } }),
    },
    updatePositionTargetPrice: {
      mutationFn: ({
        positionId,
        body,
      }: {
        positionId: string;
        body: Body<'positionsUpdatePositionTargetPrice'>;
      }) => client.positionsUpdatePositionTargetPrice(body, { params: { positionId } }),
    },
  };
}
